<template>
  <head>
    <link rel="shortcut icon" href="assets/images/favicon.svg">
    <link href="assets/css/bootstrap.min.css" id="bootstrap-style" rel="stylesheet" type="text/css" />
    <link href="assets/css/icons.min.css" rel="stylesheet" type="text/css" />
    <link href="assets/css/app.min.css" id="app-style" rel="stylesheet" type="text/css" />
    <link href="assets/css/custom.css" id="app-style" rel="stylesheet" type="text/css" />
  </head>

  <body @contextmenu.prevent data-sidebar="dark">
  <div id="layout-wrapper">
    <NavigationBar />

    <div id="content" class="content-minimize">
      <div class="page-content">
        <div class="container-fluid">

          <!-- Page Title -->
          <div class="row">
            <div class="col-12">
              <div class="page-title-box d-sm-flex align-items-center justify-content-between">
                <h4 class="mb-sm-0 font-size-18">
                  <i class="bx bx-bot"></i>CSP Payment AI Revenue Assistant
                </h4>
                <div class="page-title-right">
                  <ol class="breadcrumb m-0">
                    <li class="breadcrumb-item"><a href="javascript: void(0);" @click="goHome">Dashboards</a></li>
                    <li class="breadcrumb-item active">AI Assistant</li>
                  </ol>
                </div>
              </div>
            </div>
          </div>

          <!-- Main Chat Interface -->
          <div class="row">
            <div class="col-lg-8">
              <div class="card h-100">
                <div class="card-header bg-light">
                  <div class="d-flex justify-content-between align-items-center">
                    <h6 class="card-title mb-0">
                      <i class="bx bx-bot text-primary"></i>CSP AI Revenue Assistant
                    </h6>
                    <button @click="clearChat" class="btn btn-sm btn-outline-secondary" title="Clear conversation">
                      <i class="bx bx-trash"></i> Clear
                    </button>
                  </div>
                </div>

                <div class="card-body chat-wrapper">
                  <!-- Messages Container -->
                  <div class="messages-container">
                    <!-- Empty State -->
                    <div v-if="messages.length === 0" class="empty-state">
                      <div class="empty-icon">
                        <i class="bx bx-bot"></i>
                      </div>
                      <h6>Welcome to AI Revenue Assistant</h6>
                      <p class="text-muted small">
                        Ask me anything about your revenue data. I can help you analyze collections, generate reports, and answer complex questions.
                      </p>
                      <div class="mt-3">
                        <p class="small text-muted mb-2">Try asking:</p>
                        <div class="suggestion-chips">
                          <button @click="setSuggestion('How much was collected today?')" class="chip">
                            Collections today
                          </button>
                          <button @click="setSuggestion('Show revenue by department')" class="chip">
                            Revenue by department
                          </button>
                          <button @click="setSuggestion('What zone collected the most this month?')" class="chip">
                            Top zone this month
                          </button>
                          <button @click="setSuggestion('List unpaid invoices over 100000')" class="chip">
                            Large unpaid invoices
                          </button>
                        </div>
                      </div>
                    </div>

                    <!-- Messages -->
                    <div v-for="(msg, i) in messages" :key="i" :class="['message', msg.role]">
                      <div class="message-content">
                        <!-- Message Header -->
                        <div class="message-header">
                          <strong>{{ msg.role === 'user' ? 'You' : 'AI Assistant' }}</strong>
                          <small class="text-muted ms-2">{{ formatTime(msg.timestamp) }}</small>
                        </div>

                        <!-- Message Body -->
                        <div class="message-body">
                          {{ msg.content }}
                        </div>

                        <!-- Data Table (if available) -->
                        <div v-if="msg.data && msg.data.length > 0" class="message-data mt-2">
                          <div class="data-table">
                            <table class="table table-sm table-striped mb-0">
                              <thead>
                              <tr>
                                <th v-for="key in Object.keys(msg.data[0])" :key="key" class="text-nowrap">
                                  {{ formatColumnName(key) }}
                                </th>
                              </tr>
                              </thead>
                              <tbody>
                              <tr v-for="(row, idx) in msg.data" :key="idx">
                                <td v-for="(value, key) in row" :key="key" class="text-nowrap small">
                                  {{ formatDataValue(value) }}
                                </td>
                              </tr>
                              </tbody>
                            </table>
                          </div>
                        </div>

                        <!-- SQL Query (debug mode) -->
                        <div v-if="msg.sql && showSQLDebug" class="message-sql mt-2">
                          <small class="text-muted d-block mb-1">SQL Query:</small>
                          <code class="sql-code">{{ msg.sql }}</code>
                        </div>
                      </div>
                    </div>

                    <!-- Loading Indicator -->
                    <div v-if="loading" class="message assistant">
                      <div class="message-content">
                        <div class="d-flex align-items-center">
                          <div class="spinner-border spinner-border-sm text-primary me-2" role="status">
                            <span class="sr-only">Loading...</span>
                          </div>
                          <small>AI is thinking...</small>
                        </div>
                      </div>
                    </div>

                    <!-- Error Message -->
                    <div v-if="error" class="message assistant error-message">
                      <div class="message-content">
                        <div class="alert alert-warning mb-0">
                          <strong>Error:</strong> {{ error }}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Input Area -->
                <div class="card-footer">
                  <form @submit.prevent="sendMessage" class="d-flex gap-2">
                    <input
                        v-model="userInput"
                        type="text"
                        class="form-control"
                        placeholder="Ask about your revenue data..."
                        :disabled="loading"
                        @keydown.enter="sendMessage"
                        autocomplete="off"
                    />
                    <button
                        type="submit"
                        class="btn btn-primary"
                        :disabled="loading || !userInput.trim()"
                    >
                      <i class="bx bx-send"></i> Send
                    </button>
                  </form>
                </div>
              </div>
            </div>

            <!-- Sidebar - Sample Queries & Info -->
            <div class="col-lg-4">
              <!-- Sample Queries Card -->
              <div class="card mb-3">
                <div class="card-header bg-light">
                  <h6 class="card-title mb-0">
                    <i class="bx bx-lightbulb"></i> Sample Queries
                  </h6>
                </div>
                <div class="card-body p-0">
                  <div class="list-group list-group-flush">
                    <button
                        v-for="(query, i) in suggestedQueries"
                        :key="i"
                        @click="setSuggestion(query)"
                        class="list-group-item list-group-item-action text-start small"
                    >
                      <i class="bx bx-chevron-right"></i> {{ query }}
                    </button>
                  </div>
                </div>
              </div>

              <!-- About Card -->
              <div class="card mb-3">
                <div class="card-header bg-light">
                  <h6 class="card-title mb-0">
                    <i class="bx bx-info-circle"></i> About This Assistant
                  </h6>
                </div>
                <div class="card-body small">
                  <p class="mb-2">
                    <strong>Powered by Craft Silicon</strong> - This assistant can:
                  </p>
                  <ul class="list-unstyled">
                    <li><i class="bx bx-check text-success"></i> Query your revenue database</li>
                    <li><i class="bx bx-check text-success"></i> Analyze financial data</li>
                    <li><i class="bx bx-check text-success"></i> Generate SQL queries</li>
                    <li><i class="bx bx-check text-success"></i> Answer complex questions</li>
                    <li><i class="bx bx-check text-success"></i> Provide insights</li>
                  </ul>
                  <hr />
                  <p class="text-muted mb-0">
                    <small>All queries are processed securely on your backend. No data is shared externally.</small>
                  </p>
                </div>
              </div>

              <!-- Settings Card -->
              <div class="card">
                <div class="card-header bg-light">
                  <h6 class="card-title mb-0">
                    <i class="bx bx-sliders"></i> Settings
                  </h6>
                </div>
                <div class="card-body small">
                  <div class="form-check">
                    <input
                        v-model="showSQLDebug"
                        type="checkbox"
                        class="form-check-input"
                        id="showSQLDebug"
                    />
                    <label class="form-check-label" for="showSQLDebug">
                      Show SQL Queries
                    </label>
                  </div>
                  <hr />
                  <button @click="exportChat" class="btn btn-sm btn-outline-primary w-100">
                    <i class="bx bx-download"></i> Export Chat
                  </button>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>

    <!-- Footer -->
    <footer class="footer">
      <div class="container-fluid">
        <div class="row">
          <div class="col-sm-6">
            ©AI Revenue Assistant
          </div>
          <div class="col-sm-6">
            <div class="text-sm-end d-sm-block">
              Powered by Craft Silicon Payments
            </div>
          </div>
        </div>
      </div>
    </footer>
  </div>
  </body>
</template>

<script>
/* eslint-disable */
import NavigationBar from '@/components/Navigation';
import { biller, execute } from "@/api";

export default {
  name: 'AIAssistant',
  components: { NavigationBar },

  data() {
    return {
      userInput: '',
      messages: [],
      loading: false,
      error: null,
      showSQLDebug: false,
      suggestedQueries: [
        'How much was collected today?',
        'Show revenue by department',
        'What zone collected the most this month?',
        'List unpaid invoices over 100000',
        'Show me collections by sub-county',
        'What are pending invoices?',
        'Generate a daily collection report',
        'Which department has highest revenue?'
      ]
    };
  },

  methods: {
    goHome() {
      sessionStorage.setItem("currentPage", "dashboard");
      sessionStorage.setItem("selected", "dashboard");
      this.$router.push("dashboard");
    },

    setSuggestion(query) {
      this.userInput = query;
      this.$nextTick(() => {
        this.sendMessage();
      });
    },

    async sendMessage() {
      if (!this.userInput.trim()) return;

      this.error = null;

      const userMessage = {
        role: 'user',
        content: this.userInput,
        timestamp: new Date(),
        data: null,
        sql: null
      };

      this.messages.push(userMessage);
      const userQuery = this.userInput;
      this.userInput = '';
      this.loading = true;

      try {
        const response = await this.queryAI(userQuery);

        if (!response.success) {
          throw new Error(response.error || 'Failed to get response from AI');
        }

        const assistantMessage = {
          role: 'assistant',
          content: response.answer || 'No response',
          timestamp: new Date(),
          data: response.tableData || null,
          sql: response.sql || null
        };

        this.messages.push(assistantMessage);
      } catch (error) {
        this.error = error.message || 'Unable to process your request. Please try again.';
        const errorMessage = {
          role: 'assistant',
          content: `Error: ${error.message || 'Unable to process your request'}`,
          timestamp: new Date(),
          data: null,
          sql: null
        };
        this.messages.push(errorMessage);
      } finally {
        this.loading = false;
        this.$nextTick(() => {
          const container = document.querySelector('.messages-container');
          if (container) {
            container.scrollTop = container.scrollHeight;
          }
        });
      }
    },

    async queryAI(query) {
      const data = new FormData();
      data.append("function", "queryAIAssistant");
      data.append("query", query);
      data.append("sessionNames", sessionStorage.getItem("sessionNames") || '');
      data.append("sessionIdNo", sessionStorage.getItem("sessionIdNo") || '');

      try {
        const response = await execute(data, biller);

        // Handle the nested response structure from index.php
        // response.data has: { success, data: { success, data: { answer, tableData, sql }, error } }
        const aiResponse = response.data;

        if (aiResponse.success && aiResponse.data) {
          const aiData = aiResponse.data;
          return {
            success: aiData.success,
            answer: aiData.data?.answer || 'No response',
            tableData: aiData.data?.tableData || null,
            sql: aiData.data?.sql || null,
            error: aiData.error || null
          };
        } else {
          throw new Error(aiResponse.error || 'Failed to get response from AI');
        }
      } catch (error) {
        console.error('AI Query Error:', error);
        throw new Error(error.message || 'Failed to query AI');
      }
    },

    clearChat() {
      if (confirm('Clear all messages? This cannot be undone.')) {
        this.messages = [];
        this.userInput = '';
        this.error = null;
      }
    },

    formatTime(timestamp) {
      if (!timestamp) return '';
      const date = new Date(timestamp);
      return date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit'
      });
    },

    formatColumnName(name) {
      return name
          .split('_')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
    },

    formatDataValue(value) {
      if (value === null || value === undefined) {
        return '-';
      }

      if (typeof value === 'number') {
        if (value > 999) {
          return new Intl.NumberFormat('en-KE', {
            style: 'currency',
            currency: 'KES',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
          }).format(value);
        }
        return value.toLocaleString('en-KE');
      }

      if (value instanceof Date) {
        return value.toLocaleDateString('en-KE');
      }

      return String(value);
    },

    exportChat() {
      if (this.messages.length === 0) {
        alert('No messages to export');
        return;
      }

      const chatData = this.messages.map(msg => ({
        role: msg.role,
        content: msg.content,
        timestamp: msg.timestamp,
        sql: msg.sql
      }));

      const dataStr = JSON.stringify(chatData, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `ai-assistant-chat-${new Date().toISOString()}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
  }
};
</script>

<style scoped>
.chat-wrapper {
  display: flex;
  flex-direction: column;
  height: 600px;
  padding: 0;
}

.messages-container {
  flex: 1;
  overflow-y: auto;
  padding: 1.5rem;
  background-color: #f9f9fa;
}

.messages-container::-webkit-scrollbar {
  width: 6px;
}

.messages-container::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 10px;
}

.messages-container::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 10px;
}

.messages-container::-webkit-scrollbar-thumb:hover {
  background: #555;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  text-align: center;
  padding: 2rem;
}

.empty-icon {
  font-size: 4rem;
  color: #e9ecef;
  margin-bottom: 1rem;
  opacity: 0.5;
}

.message {
  margin-bottom: 1.5rem;
  display: flex;
  animation: slideIn 0.3s ease-out;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.message.user {
  justify-content: flex-end;
}

.message.assistant {
  justify-content: flex-start;
}

.message-content {
  max-width: 70%;
  padding: 1rem;
  border-radius: 0.5rem;
  word-wrap: break-word;
}

.message.user .message-content {
  background-color: #007bff;
  color: white;
  border: none;
}

.message.assistant .message-content {
  background-color: white;
  border: 1px solid #dee2e6;
  color: #2d3748;
}

.message-header {
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;
  font-size: 0.875rem;
  font-weight: 500;
}

.message.user .message-header {
  color: rgba(255, 255, 255, 0.9);
  justify-content: flex-end;
}

.message.user .message-header small {
  color: rgba(255, 255, 255, 0.7) !important;
}

.message.assistant .message-header {
  color: #495057;
}

.message-body {
  line-height: 1.5;
  margin: 0;
}

.message-data {
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid rgba(0, 0, 0, 0.1);
}

.message.user .message-data {
  border-top-color: rgba(255, 255, 255, 0.2);
}

.data-table {
  background-color: rgba(0, 0, 0, 0.05);
  border-radius: 0.25rem;
  overflow-x: auto;
  max-height: 300px;
  overflow-y: auto;
}

.message.user .data-table {
  background-color: rgba(255, 255, 255, 0.1);
}

.data-table table {
  margin: 0;
  width: 100%;
}

.data-table thead {
  position: sticky;
  top: 0;
  background-color: rgba(0, 0, 0, 0.1);
}

.message.user .data-table thead {
  background-color: rgba(255, 255, 255, 0.2);
}

.data-table th,
.data-table td {
  padding: 0.5rem;
  font-size: 0.75rem;
  white-space: nowrap;
}

.data-table th {
  font-weight: 600;
  color: #2d3748;
}

.message.user .data-table th {
  color: white;
}

.message-sql {
  margin-top: 1rem;
  padding: 0.75rem;
  background-color: rgba(0, 0, 0, 0.05);
  border-radius: 0.25rem;
  border-left: 3px solid #007bff;
}

.sql-code {
  display: block;
  background-color: rgba(0, 0, 0, 0.03);
  padding: 0.5rem;
  border-radius: 0.25rem;
  font-family: 'Courier New', monospace;
  font-size: 0.7rem;
  color: #6c757d;
  word-break: break-all;
  max-height: 100px;
  overflow-y: auto;
}

.error-message {
  justify-content: flex-start;
}

.error-message .alert {
  margin: 0;
  padding: 0.75rem;
}

.suggestion-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.5rem;
  justify-content: center;
}

.chip {
  background-color: #e9ecef;
  border: 1px solid #dee2e6;
  border-radius: 20px;
  padding: 0.5rem 1rem;
  font-size: 0.85rem;
  cursor: pointer;
  transition: all 0.2s;
  color: #495057;
  font-weight: 500;
}

.chip:hover {
  background-color: #007bff;
  color: white;
  border-color: #007bff;
  transform: translateY(-1px);
}

.chip:active {
  transform: translateY(0);
}

.list-group-item {
  border: none;
  border-bottom: 1px solid #e9ecef;
  color: #495057;
  cursor: pointer;
  transition: background-color 0.2s;
  padding: 0.75rem 1rem;
}

.list-group-item:last-child {
  border-bottom: none;
}

.list-group-item:hover {
  background-color: #f8f9fa;
  color: #007bff;
}

.list-group-item i {
  margin-right: 0.5rem;
}

.card-footer {
  border-top: 1px solid #e9ecef;
  padding: 1rem;
  background-color: #fff;
}

.gap-2 {
  gap: 0.5rem;
}

.form-control {
  border: 1px solid #dee2e6;
  border-radius: 0.25rem;
}

.form-control:focus {
  border-color: #007bff;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

.form-control:disabled {
  background-color: #e9ecef;
  cursor: not-allowed;
}

.btn {
  transition: all 0.2s;
}

.btn-primary:disabled,
.btn-primary:disabled:hover {
  opacity: 0.65;
  cursor: not-allowed;
}

.btn-outline-secondary {
  color: #6c757d;
  border-color: #dee2e6;
}

.btn-outline-secondary:hover {
  background-color: #f8f9fa;
  border-color: #adb5bd;
  color: #495057;
}

.spinner-border-sm {
  width: 1rem;
  height: 1rem;
  border-width: 0.2em;
}

.footer {
  background-color: #f8f9fa;
  border-top: 1px solid #e9ecef;
  padding: 1.5rem 0;
  margin-top: 3rem;
  font-size: 0.875rem;
  color: #6c757d;
}

@media (max-width: 992px) {
  .message-content {
    max-width: 80%;
  }
}

@media (max-width: 768px) {
  .chat-wrapper {
    height: 400px;
  }

  .message-content {
    max-width: 90%;
  }

  .data-table th,
  .data-table td {
    padding: 0.35rem;
    font-size: 0.65rem;
  }

  .card-body {
    padding: 0.75rem;
  }
}

@media (max-width: 576px) {
  .chat-wrapper {
    height: 300px;
  }

  .message {
    margin-bottom: 1rem;
  }

  .message-content {
    max-width: 100%;
    padding: 0.75rem;
  }

  .empty-icon {
    font-size: 3rem;
  }
}

.text-nowrap {
  white-space: nowrap;
}

.me-2 {
  margin-right: 0.5rem;
}

.ms-2 {
  margin-left: 0.5rem;
}

.mb-0 {
  margin-bottom: 0;
}

.mb-2 {
  margin-bottom: 0.5rem;
}

.mb-3 {
  margin-bottom: 1rem;
}

.mt-2 {
  margin-top: 0.5rem;
}

.mt-3 {
  margin-top: 1rem;
}

.w-100 {
  width: 100%;
}

.d-flex {
  display: flex;
}

.gap-2 {
  gap: 0.5rem;
}

.text-success {
  color: #28a745;
}

.text-muted {
  color: #6c757d;
}

.text-primary {
  color: #007bff;
}

.text-sm-end {
  text-align: right;
}
</style>